import { Box, Flex, Progress, Stack, Text } from '@chakra-ui/react';
import { differenceInDays, format } from 'date-fns';

import type { UserAssignment } from '@/client/types/dashboard/UserAssignment';
import { setContentUrl } from '@/client/components/learners/dashboard/utils/setContentUrl';
import { useLearnerDashboard } from '@/client/components/learners/dashboard/hooks/useLearnerDashboard';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function AssignmentDataCard({ assignment }: { assignment: UserAssignment }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { contentTypeTranslationMap } = useLearnerDashboard();

  const imageUrl =
    assignment.assigned_content[0].content_id?.images?.explore ||
    assignment.assigned_content[0].content_id.image;

  const multipleAssignedContent = assignment.assigned_content.length > 1;

  const overDueDays = assignment.due_date
    ? differenceInDays(new Date(assignment.due_date), new Date())
    : null;

  const isOverdue = overDueDays && overDueDays < 1;

  const nonStackedBoxStyles = {
    height: '250px',
    border: '1px solid',
    borderColor: 'neutral.100',
    backgroundColor: 'white',
    borderRadius: 16,
  };

  const isStacked = assignment.assigned_content.length > 1;

  const cardData = () => (
    <>
      <Flex
        width="full"
        backgroundColor={isOverdue ? '#FEF1F2' : 'neutral.50'}
        justifyContent="center"
        padding={2}
        borderTopRadius={16}
      >
        <Text
          fontSize="11px"
          fontWeight={600}
          textTransform="uppercase"
          color={assignment.due_date ? (isOverdue ? '#ff1f57' : 'neutral.1200') : 'neutral.600'}
        >
          {assignment.due_date
            ? isOverdue
              ? overDueDays === -1
                ? t('learners.dashboard.dayOverdue')
                : t('learners.dashboard.daysOverdue', { days: -overDueDays })
              : t('learners.dashboard.dueDate', {
                  date: format(new Date(assignment.due_date), 'MMM d, yyyy'),
                })
            : t('learners.dashboard.noDueDate')}
        </Text>
      </Flex>

      <Box
        width="full"
        height="130px"
        background={
          !imageUrl || imageUrl?.endsWith('undefined')
            ? 'url(/public/images/default-assignment-background.svg)'
            : 'url(' + imageUrl + ')'
        }
        backgroundSize="cover"
        backgroundPosition="center center"
        backgroundColor="brand.primary"
        backgroundBlendMode={!imageUrl || imageUrl?.endsWith('undefined') ? 'overlay' : 'normal'}
      >
        {multipleAssignedContent && (
          <Flex direction="column" height="full" width="full" justifyContent="flex-end" padding={2}>
            <Progress
              value={
                (assignment.completed_content.length / assignment.assigned_content.length) * 100
              }
              height="6px"
              borderRadius={16}
            />
          </Flex>
        )}
      </Box>

      <Stack padding={4}>
        <Text fontSize="11px" fontWeight={600} textTransform="uppercase" color="neutral.800">
          {multipleAssignedContent
            ? t('learners.dashboard.completed', {
                count: assignment.completed_content.length,
              })
            : contentTypeTranslationMap[assignment.assigned_content[0].content_id.type]}
        </Text>

        <Text
          fontSize="16px"
          fontWeight={500}
          color="baseBlack"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {multipleAssignedContent
            ? t('learners.dashboard.multiItemAssignmentCount', {
                count: assignment.assigned_content.length,
              })
            : assignment.assigned_content[0].content_id.title ||
              assignment.assigned_content[0].content_id.name}
        </Text>
      </Stack>
    </>
  );

  if (isStacked) {
    return (
      <Box
        height="250px"
        position="relative"
        borderRadius={16}
        _hover={{ cursor: 'pointer', boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, .1)' }}
        transition="box-shadow 400ms"
        onClick={() =>
          navigate('/dashboard/assignments/assignment-details', { state: { assignment } })
        }
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          border="1px solid"
          borderColor="neutral.100"
          borderRadius={16}
        />

        <Box
          position="absolute"
          top={0}
          left={0}
          right={1}
          bottom={1}
          border="1px solid"
          borderColor="neutral.100"
          borderRadius={16}
        />

        <Box
          position="absolute"
          top={0}
          left={0}
          right={2}
          bottom={2}
          border="1px solid"
          borderColor="neutral.100"
          borderRadius={16}
        >
          {cardData()}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={nonStackedBoxStyles}
      _hover={{ cursor: 'pointer', boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, .1)' }}
      transition="box-shadow 400ms"
      onClick={() =>
        navigate(
          setContentUrl(
            assignment.assigned_content[0].content_id._id,
            assignment.assigned_content[0].content_id.type,
            assignment.assigned_content[0].user_content_id,
          ),
        )
      }
    >
      {cardData()}
    </Box>
  );
}
